import * as THREE from "three";

export type PanelButton = {
  label: string;
  onSelect: () => void;
};

type ButtonRec = {
  mesh: THREE.Mesh;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  button: PanelButton;
  hover: boolean;
};

export class ToolPanel {
  readonly group = new THREE.Group();
  private buttons: ButtonRec[] = [];
  private raycaster = new THREE.Raycaster();
  private hovered = new Map<THREE.Object3D, ButtonRec | null>();
  private hitDistances = new Map<THREE.Object3D, number | null>();

  constructor(title: string, buttons: PanelButton[]) {
    const padding = 0.04;
    const btnW = 0.46;
    const btnH = 0.10;
    const gap = 0.02;
    const titleH = 0.06;
    const totalH = titleH + padding + buttons.length * btnH + (buttons.length - 1) * gap + padding * 2;
    const totalW = btnW + padding * 2;

    const bg = new THREE.Mesh(
      new THREE.PlaneGeometry(totalW, totalH),
      new THREE.MeshBasicMaterial({ color: 0x111118, transparent: true, opacity: 0.92 }),
    );
    bg.position.z = -0.002;
    this.group.add(bg);

    const titleMesh = makeTextMesh(title, totalW - padding * 2, titleH, 28, "#9ca3af");
    titleMesh.position.set(0, totalH / 2 - padding - titleH / 2, 0);
    this.group.add(titleMesh);

    let y = totalH / 2 - padding - titleH - padding - btnH / 2;
    for (const b of buttons) {
      const rec = this.makeButton(b, btnW, btnH);
      rec.mesh.position.set(0, y, 0);
      rec.mesh.userData.panelButton = rec;
      this.buttons.push(rec);
      this.group.add(rec.mesh);
      y -= btnH + gap;
    }
  }

  private makeButton(button: PanelButton, w: number, h: number): ButtonRec {
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = Math.round((h / w) * 1024);
    const ctx = canvas.getContext("2d")!;
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    const rec: ButtonRec = { mesh, canvas, ctx, texture, button, hover: false };
    drawButton(rec);
    return rec;
  }

  /**
   * Place the panel ~1m in front of the camera at eye level, facing it.
   * Reads camera world pose — only correct AFTER the first XR frame has
   * rendered. For panel placement on session start, prefer placeInFrontOfPose
   * with a viewer pose pulled from XRFrame directly.
   */
  placeInFrontOf(camera: THREE.Camera, distance = 1.0): void {
    const camPos = new THREE.Vector3();
    const camDir = new THREE.Vector3();
    camera.getWorldPosition(camPos);
    camera.getWorldDirection(camDir);
    this.group.position.copy(camPos).add(camDir.multiplyScalar(distance));
    this.group.lookAt(camPos);
  }

  /**
   * Place the panel using an explicit head pose (typically pulled from
   * XRFrame.getViewerPose). Use this on session start, before Three.js has
   * synced the camera to the headset.
   */
  placeInFrontOfPose(
    headPos: THREE.Vector3,
    headQuat: THREE.Quaternion,
    distance = 1.0,
  ): void {
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(headQuat);
    this.group.position.copy(headPos).add(fwd.multiplyScalar(distance));
    this.group.lookAt(headPos);
  }

  /**
   * Update the hover state for a single controller. Call each frame for each
   * active controller. Returns the hovered button (if any).
   */
  updateHover(controller: THREE.Object3D): PanelButton | null {
    const origin = new THREE.Vector3();
    const dir = new THREE.Vector3();
    controller.getWorldPosition(origin);
    // Three.js controllers point along -Z in their local frame.
    dir.set(0, 0, -1).applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion()));
    this.raycaster.set(origin, dir);

    const hits = this.raycaster.intersectObjects(
      this.buttons.map((b) => b.mesh),
      false,
    );
    this.hitDistances.set(controller, hits.length > 0 ? hits[0].distance : null);
    const newHover = hits.length > 0
      ? (hits[0].object.userData.panelButton as ButtonRec)
      : null;

    const previous = this.hovered.get(controller) ?? null;
    if (newHover !== previous) {
      // un-hover previous if no other controller is hovering it
      if (previous) {
        const stillHovered = [...this.hovered.values()].some(
          (r) => r === previous && controller !== keyForRec(this.hovered, previous),
        );
        if (!stillHovered) {
          previous.hover = false;
          drawButton(previous);
        }
      }
      if (newHover) {
        newHover.hover = true;
        drawButton(newHover);
      }
      this.hovered.set(controller, newHover);
    }

    return newHover ? newHover.button : null;
  }

  /**
   * Forget any hover state for a controller (e.g., on selectstart that landed
   * outside the panel). Call after a button has been selected.
   */
  clearHoverFor(controller: THREE.Object3D): void {
    this.hovered.delete(controller);
  }

  /** Returns the button currently hovered by this controller, if any. */
  hoveredBy(controller: THREE.Object3D): PanelButton | null {
    const rec = this.hovered.get(controller);
    return rec ? rec.button : null;
  }

  /**
   * Distance from the controller to the panel along the pointing ray, or null
   * if the ray isn't hitting any button. Use to scale a pointer beam.
   */
  hoverDistance(controller: THREE.Object3D): number | null {
    return this.hitDistances.get(controller) ?? null;
  }

  /**
   * Update the label of the button at the given index and redraw its texture.
   * The button's onSelect callback is preserved.
   */
  setButtonLabel(index: number, label: string): void {
    const rec = this.buttons[index];
    if (!rec) return;
    rec.button.label = label;
    drawButton(rec);
  }
}

function keyForRec<K, V>(map: Map<K, V>, value: V): K | undefined {
  for (const [k, v] of map) if (v === value) return k;
  return undefined;
}

function makeTextMesh(text: string, w: number, h: number, _fontPx: number, color: string): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = Math.round((h / w) * 1024);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = color;
  ctx.font = `600 ${Math.round(canvas.height * 0.55)}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true }),
  );
}

function drawButton(rec: ButtonRec): void {
  const { ctx, canvas, button, hover } = rec;
  const w = canvas.width, h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = hover ? "#4f46e5" : "#252533";
  roundRect(ctx, 8, 8, w - 16, h - 16, 28);
  ctx.fill();
  ctx.fillStyle = "#e6e6ea";
  ctx.font = `600 ${Math.round(h * 0.42)}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(button.label, w / 2, h / 2);
  rec.texture.needsUpdate = true;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
